import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import TaskCreateModal from './TaskCreateModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_COLORS = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-red-100 text-red-700',
};

const STATUS_META = {
  todo: { label: 'À faire', color: 'bg-slate-100 text-slate-700', icon: 'bi-circle' },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700', icon: 'bi-arrow-clockwise' },
  done: { label: 'Terminée', color: 'bg-emerald-100 text-emerald-700', icon: 'bi-check-circle-fill' },
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isOverdue = (t) => {
  if (!t.due_date || t.status === 'done') return false;
  return new Date(t.due_date) < new Date();
};

/**
 * Section "Tâches liées" pour fiche bien ou fiche contact.
 * Props :
 *   - propertyId : si fourni, filtre par linked_property_id
 *   - contactId  : si fourni, filtre par linked_contact_id
 *   - defaultTaskTitle : titre pré-rempli lors de la création (ex : "Suivi : nom du contact")
 */
const LinkedTasksSection = ({ propertyId, contactId, defaultTaskTitle = '' }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHidden, setShowHidden] = useState(false); // afficher les terminées
  const [showCreate, setShowCreate] = useState(false);

  const loadTasks = useCallback(async () => {
    if (!propertyId && !contactId) return;
    setLoading(true);
    try {
      const params = {};
      if (propertyId) params.linked_property_id = propertyId;
      if (contactId) params.linked_contact_id = contactId;
      const res = await axios.get(`${API}/tasks`, { params });
      setTasks(res.data || []);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [propertyId, contactId]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Refresh quand une tâche change ailleurs dans l'app
  useEffect(() => {
    const handler = () => loadTasks();
    window.addEventListener('tasks-changed', handler);
    return () => window.removeEventListener('tasks-changed', handler);
  }, [loadTasks]);

  const handleToggle = async (t) => {
    try {
      await axios.post(`${API}/tasks/${t.id}/complete`);
      loadTasks();
      window.dispatchEvent(new Event('tasks-changed'));
    } catch {
      toast.error('Erreur changement de statut');
    }
  };

  const handleSetStatus = async (t, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${t.id}`, { status: newStatus });
      loadTasks();
      window.dispatchEvent(new Event('tasks-changed'));
    } catch {
      toast.error('Erreur changement de statut');
    }
  };

  const visible = showHidden ? tasks : tasks.filter((t) => t.status !== 'done');
  const activeCount = tasks.filter((t) => t.status !== 'done').length;
  const doneCount = tasks.length - activeCount;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="linked-tasks-section">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <i className="bi bi-check2-square text-blue-600 text-lg"></i>
          <h3 className="font-bold text-slate-800">Tâches liées</h3>
          {activeCount > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-semibold">
              {activeCount} active{activeCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="text-xs font-semibold text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
          data-testid="linked-tasks-add-btn"
        >
          <i className="bi bi-plus-lg"></i>Ajouter
        </button>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="text-center text-sm text-slate-500 py-4">
            <i className="bi bi-arrow-clockwise animate-spin mr-2"></i>Chargement…
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-6">
            <i className="bi bi-clipboard-check text-3xl text-slate-300 block mb-2"></i>
            <p className="text-sm text-slate-500 mb-3">
              {tasks.length === 0 ? 'Aucune tâche liée' : 'Toutes les tâches sont terminées'}
            </p>
            <button
              onClick={() => setShowCreate(true)}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold"
              data-testid="linked-tasks-empty-add-btn"
            >
              <i className="bi bi-plus-lg mr-1"></i>Créer une tâche
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 -mx-4">
            {visible.map((t) => {
              const overdue = isOverdue(t);
              const status = STATUS_META[t.status] || STATUS_META.todo;
              return (
                <li key={t.id} className={`px-4 py-3 ${t.status === 'done' ? 'opacity-60' : ''}`} data-testid={`linked-task-${t.id}`}>
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => handleToggle(t)}
                      className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition ${
                        t.status === 'done'
                          ? 'bg-emerald-500 border-emerald-500 text-white'
                          : 'border-slate-300 hover:border-emerald-500'
                      }`}
                      title={t.status === 'done' ? 'Rouvrir' : 'Marquer terminée'}
                      aria-label="Toggle"
                    >
                      {t.status === 'done' && <i className="bi bi-check-lg text-xs"></i>}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`font-medium text-sm text-slate-800 ${t.status === 'done' ? 'line-through text-slate-500' : ''}`}>
                          {t.title}
                        </span>
                        {t.priority === 'high' && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${PRIORITY_COLORS.high}`}>Haute</span>
                        )}
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${status.color}`}>
                          <i className={`bi ${status.icon}`}></i>{status.label}
                        </span>
                        {overdue && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-red-100 text-red-700">
                            <i className="bi bi-exclamation-triangle mr-0.5"></i>Retard
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap items-center gap-2.5">
                        {t.due_date && (
                          <span><i className="bi bi-calendar3 mr-1"></i>{formatDate(t.due_date)}</span>
                        )}
                        {t.assigned_to_user_ids?.length === 0 ? (
                          <span><i className="bi bi-people-fill mr-1"></i>Toute l'agence</span>
                        ) : (
                          <span><i className="bi bi-person-fill mr-1"></i>{t.assigned_to_user_ids.length} assigné(s)</span>
                        )}
                      </div>
                      {t.description && (
                        <p className="text-[11px] text-slate-600 mt-1 line-clamp-2">{t.description}</p>
                      )}
                      {t.status !== 'done' && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-1">
                          {t.status === 'todo' && (
                            <button
                              onClick={() => handleSetStatus(t, 'in_progress')}
                              className="text-[10px] px-2 py-0.5 rounded bg-amber-50 hover:bg-amber-100 text-amber-700 font-semibold inline-flex items-center gap-1"
                              data-testid={`linked-task-start-${t.id}`}
                            >
                              <i className="bi bi-arrow-clockwise"></i>Démarrer
                            </button>
                          )}
                          <button
                            onClick={() => handleSetStatus(t, 'done')}
                            className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold inline-flex items-center gap-1"
                            data-testid={`linked-task-done-${t.id}`}
                          >
                            <i className="bi bi-check-circle"></i>Terminer
                          </button>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => navigate('/taches')}
                      className="text-slate-400 hover:text-blue-500 p-1"
                      title="Ouvrir dans la page Tâches"
                    >
                      <i className="bi bi-box-arrow-up-right text-xs"></i>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {doneCount > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100 text-center">
            <button
              onClick={() => setShowHidden((v) => !v)}
              className="text-[11px] text-slate-500 hover:text-slate-700"
              data-testid="linked-tasks-toggle-hidden"
            >
              {showHidden ? 'Masquer' : 'Afficher'} les tâches terminées ({doneCount})
            </button>
          </div>
        )}
      </div>

      {showCreate && (
        <TaskCreateModal
          defaultLinkedPropertyId={propertyId || null}
          defaultLinkedContactId={contactId || null}
          defaultTitle={defaultTaskTitle}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadTasks(); }}
        />
      )}
    </div>
  );
};

export default LinkedTasksSection;
